/**
 * The world, either filling the page or sitting in a window of its own.
 *
 * One canvas either way. It is never moved from one parent to another, because
 * a canvas taken out of the document and put back is a canvas with a new
 * drawing context and a world that has to be built again from nothing: what
 * changes is where the frame around it sits and how big it is, and the renderer
 * measures its own box every time that changes.
 *
 * As a panel it gets a bar to drag it by and a grip on every corner, and the
 * rest of the interface goes on floating wherever somebody has put it. That is
 * the whole point of it: the world stops being the thing everything else has to
 * work around and becomes another panel to arrange.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { useDraggableWindow } from './draggable';
import { CORNERS, CORNER_CURSORS, resizeFrom, roomFor, type Corner } from './resize';

/** How small the world may be dragged, and how it starts. */
const LEAST = { width: 480, height: 320 };
const SIZE_KEY = 'inverse.worldPanel.size';

function storedSize(): { width: number; height: number } {
  try {
    const raw = window.localStorage.getItem(SIZE_KEY);
    if (raw) {
      const held = JSON.parse(raw) as Partial<{ width: number; height: number }>;
      if (typeof held.width === 'number' && typeof held.height === 'number') {
        return {
          width: Math.max(LEAST.width, held.width),
          height: Math.max(LEAST.height, held.height),
        };
      }
    }
  } catch {
    // A size that cannot be read is a size nobody chose.
  }

  return {
    width: Math.max(LEAST.width, Math.round(window.innerWidth * 0.66)),
    height: Math.max(LEAST.height, Math.round(window.innerHeight * 0.7)),
  };
}

export function WorldFrame({
  panelled,
  children,
}: {
  /** Whether the world is a window of its own rather than the whole page. */
  panelled: boolean;
  children: ReactNode;
}): JSX.Element {
  const [size, setSize] = useState(storedSize);

  const home = useCallback(
    (box: { width: number; height: number }) => ({
      x: Math.max(0, Math.round((window.innerWidth - box.width) / 2)),
      y: Math.max(0, Math.round((window.innerHeight - box.height) / 3)),
    }),
    [],
  );

  const { attach, style, onPointerDown, nudge } = useDraggableWindow('world', { home });

  const resize =
    (corner: Corner) =>
    (event: React.PointerEvent): void => {
      event.preventDefault();
      event.stopPropagation();

      const frame = (event.currentTarget as HTMLElement).closest('.world-frame');
      let last = { x: event.clientX, y: event.clientY };
      let held = { ...size };

      const move = (moved: PointerEvent): void => {
        const dx = moved.clientX - last.x;
        const dy = moved.clientY - last.y;
        last = { x: moved.clientX, y: moved.clientY };

        const rect = frame?.getBoundingClientRect();
        const room = rect
          ? roomFor({ x: rect.x, y: rect.y, width: rect.width, height: rect.height }, corner, {
              width: window.innerWidth,
              height: window.innerHeight,
            })
          : Infinity;

        const next = resizeFrom({ x: 0, y: 0, ...held }, corner, dx, dy, {
          minWidth: LEAST.width,
          minHeight: LEAST.height,
          maxWidth: held.width + room,
          maxHeight: held.height + room,
        });

        if (next.width === held.width && next.height === held.height) return;
        held = { width: next.width, height: next.height };

        setSize(held);
        // Whatever the resize did to the near corner, undo for the far one, so
        // the corner opposite the one being pulled stands still.
        if (next.x !== 0 || next.y !== 0) nudge(next.x, next.y);
      };

      const done = (): void => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', done);
        window.localStorage.setItem(SIZE_KEY, JSON.stringify(held));
      };

      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', done);
    };

  // The same children in the same order whether it is a panel or the page. A
  // canvas that changes place among its siblings is a canvas React throws away
  // and builds again, and the world is drawn on the one that was thrown away:
  // putting the bar first turned the panel black. So the world stays first and
  // the bar and the grips are always there, shown or not.
  return (
    <div
      className={panelled ? 'world-frame panelled' : 'world-frame'}
      ref={attach}
      style={panelled ? { ...style, width: size.width, height: size.height } : undefined}
    >
      {children}

      <div className="world-bar" onPointerDown={onPointerDown} title="Drag to move the world">
        <span>Game View</span>
      </div>

      {CORNERS.map((corner) => (
        <div
          key={corner}
          className={`world-grip ${corner}`}
          style={{ cursor: CORNER_CURSORS[corner] }}
          title="Drag to resize the world"
          onPointerDown={resize(corner)}
          aria-hidden
        />
      ))}
    </div>
  );
}
